import React from "react";
import ExlyModal from "common/Components/ExlyModal";
import { Button } from "@my-scoot/component-library-legacy";
import GetAppRoundedIcon from "@material-ui/icons/GetAppRounded";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  QR_SELECTION_OPTIONS,
  QR_PAYMENT_TYPES,
  MODAL_WIDTHS,
  QR_MODAL_TITLES,
  getQrImageUrl,
} from "./QrModal.constants";
import { getTargetUrl, handleQrDownload } from "./QrModal.utils";
import { useQrModal } from "./useQrModal";
import ExlyImage from "features/Common/modules/ExlyImage/ExlyImage";

const QrModal = ({ open, onClose, record, qrUrl }) => {
  const {
    selectedType,
    apiQrUrl,
    apiQrImageUrl,
    isLoading,
    error,
    handleSelectType,
    handleCloseModal,
  } = useQrModal(record, onClose);

  const displayUrl = apiQrUrl || record?.qr_url || qrUrl;
  const targetUrl = apiQrUrl
    ? apiQrUrl
    : getTargetUrl(displayUrl, selectedType);
  const qrImageUrl = apiQrImageUrl
    ? apiQrImageUrl
    : getQrImageUrl(targetUrl, 300);

  const onDownloadClick = () => {
    handleQrDownload(
      apiQrImageUrl || displayUrl,
      selectedType,
      record?.title || record?.name,
      !!apiQrImageUrl
    );
  };

  const selectedOptionTitle =
    QR_SELECTION_OPTIONS.find((opt) => opt.id === selectedType)?.title ||
    "Payment";
  const itemTitle = record?.title || record?.name || "Offering";
  const qrAltText = `${selectedOptionTitle} QR code for ${itemTitle}`;

  return (
    <ExlyModal
      open={open}
      onClose={handleCloseModal}
      title={
        selectedType
          ? QR_MODAL_TITLES.SCAN_OR_DOWNLOAD
          : QR_MODAL_TITLES.SELECT_TYPE
      }
      desktopModalWidth={MODAL_WIDTHS.QR}
      padded={false}
      customFooter={<></>}
    >
      <>
        <div className="tw-flex tw-items-start tw-gap-3 tw-p-3.5 tw-px-4 tw-bg-[#EFF8FF]">
          <InfoOutlinedIcon className="!tw-text-[20px] tw-flex-shrink-0 tw-mt-0.5" />
          <div className="tw-text-[13px] tw-leading-[18px] tw-text-left">
            <span className="tw-font-semibold">Domestic Payments Only:</span> QR
            code payments are supported only for domestic transactions in India
            (INR ₹).
          </div>
        </div>

        <div className="tw-pb-6 tw-px-6 tw-flex tw-flex-col">
          {!selectedType ? (
            <div>
              <div className="tw-text-[15px] tw-font-medium tw-text-[#475467] tw-mb-5 tw-text-left">
                Choose how you want your customers to pay
              </div>
              <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                {QR_SELECTION_OPTIONS.map((option) => (
                  <div
                    key={option.id}
                    className={`tw-flex tw-items-start tw-gap-4 tw-p-4 tw-border tw-border-solid tw-border-[#eaecf0] tw-rounded-xl tw-transition-all tw-duration-200 ${
                      option.disabled
                        ? "tw-bg-[#f8fafc] tw-cursor-not-allowed"
                        : "tw-bg-white tw-cursor-pointer hover:tw-border-[#7f56d9] hover:tw-bg-[#f9f5ff] hover:tw-shadow-md"
                    }`}
                    onClick={() =>
                      !option.disabled && handleSelectType(option.id)
                    }
                  >
                    <div
                      className={`tw-flex tw-items-center tw-justify-center tw-w-11 tw-h-11 tw-rounded-lg tw-flex-shrink-0 ${
                        option.disabled ? "tw-opacity-60 " : ""
                      }${
                        option.id === QR_PAYMENT_TYPES.INSTANT_DELIVERY_QR
                          ? "tw-bg-[#f5f8ff] tw-text-[#2f80ed]"
                          : "tw-bg-[#fdf2fa] tw-text-[#c11574]"
                      }`}
                    >
                      {option.id === QR_PAYMENT_TYPES.INSTANT_DELIVERY_QR ? (
                        <PhoneAndroidIcon className="!tw-text-[24px]" />
                      ) : (
                        <AccountBalanceIcon className="!tw-text-[24px]" />
                      )}
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-1 tw-flex-1">
                      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                        <span
                          className={`tw-text-[15px] tw-font-semibold ${
                            option.disabled
                              ? "tw-text-[#667085]"
                              : "tw-text-[#1d2939]"
                          }`}
                        >
                          {option.title}
                        </span>
                        {option.badgeText && (
                          <span className="tw-text-[11px] tw-font-semibold tw-px-2.5 tw-py-0.5 tw-rounded-full tw-bg-[#FFFAEB] tw-text-[#B54708] tw-border tw-border-solid tw-border-[#FEDF89] tw-tracking-wide tw-shadow-xs tw-opacity-100">
                            {option.badgeText}
                          </span>
                        )}
                      </div>
                      <div
                        className={`tw-text-[13px] tw-leading-[18px] ${
                          option.disabled
                            ? "tw-text-[#98A2B3]"
                            : "tw-text-[#667085]"
                        }`}
                      >
                        {option.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="tw-flex tw-flex-col tw-w-full tw-p-4 sm:tw-p-6 tw-box-border">
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-4 tw-w-full min-h-[220px]">
                  {isLoading ? (
                    <div className="tw-flex tw-items-center tw-justify-center tw-h-[180px] tw-w-[180px]">
                      <CircularProgress color="primary" />
                    </div>
                  ) : error ? (
                    <div className="tw-text-[14px] tw-text-red-500 tw-text-center tw-p-4">
                      {error}
                    </div>
                  ) : (
                    <>
                      <div className="tw-p-4 tw-border tw-border-solid tw-border-[#eaecf0] tw-rounded-xl tw-bg-white tw-shadow-sm tw-mb-4">
                        <ExlyImage
                          src={qrImageUrl}
                          alt={qrAltText}
                          loading="lazy"
                          className="tw-w-[180px] tw-block"
                        />
                      </div>
                      <div className="tw-text-[13px] tw-text-[#667085] tw-text-center">
                        Scan this QR code using any UPI app or camera to pay
                      </div>
                    </>
                  )}
                </div>
                <div className="tw-flex tw-items-center tw-justify-center tw-w-full tw-mt-4">
                  <Button
                    className="!tw-w-full !tw-max-w-md !tw-rounded-lg !tw-font-medium !tw-h-11"
                    onClick={onDownloadClick}
                    disabled={isLoading || !!error}
                    variant="contained"
                    color="primary"
                    startIcon={<GetAppRoundedIcon />}
                  >
                    Download QR Payment Link
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </>
    </ExlyModal>
  );
};

export default QrModal;
